import { ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import {
  closerToZero,
  getBoundaryPositions,
  getBoundedValue,
  getDefaultPosition,
  getFinalPosition,
  getCenterPosition,
  mouseTargetPosition,
  requestElementUpdate,
  updateImageTransform,
  updatePictureWid,
} from '~/modules/image-zoomer/utils/zoomerUtils';

/** @typedef {import('../types').CustomZoomEvent} CustomZoomEvent */
/** @typedef {import('../types').ImagePosition} ImagePosition */
/** @typedef {import('../types').ZoomStates} ZoomStates */

const DEFAULT_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const ANIMATION_TIME = 300;
const INITIAL_MAGNITUDE = 10;
const VELOCITY_OFFSET = -60;
const VELOCITY_MAX = 1.5;
const KEYBOARD_MOVEMENT_VELOCITY = 50;
const MOVEMENT_MAP = {
  ArrowUp: { vert: KEYBOARD_MOVEMENT_VELOCITY, hor: 0 },
  ArrowDown: { vert: -KEYBOARD_MOVEMENT_VELOCITY, hor: 0 },
  ArrowLeft: { vert: 0, hor: KEYBOARD_MOVEMENT_VELOCITY },
  ArrowRight: { vert: 0, hor: -KEYBOARD_MOVEMENT_VELOCITY },
};

/**
 *
 * @param {import('vue').Ref<HTMLElement>} imageContainer
 * @returns {{
 *     zoomStates: import('vue').Ref<ZoomStates>
 *     isAnimating: import('vue').Ref<boolean>
 *     tapStart: (event: Event | TouchEvent) => void
 *     tap: (event: MouseEvent | TouchEvent) => void
 *     pinchStart: (event: TouchEvent) => void
 *     pinch: (event: CustomZoomEvent) => void
 *     pinchEnd: (event: CustomZoomEvent) => void
 *     mouseMove: (event: MouseEvent) => void
 *     pan: (event: CustomZoomEvent) => void
 *     panEnd: (event: CustomZoomEvent) => void
 *     onEnter: (event: KeyboardEvent) => void
 *     onArrowPressed: (event: KeyboardEvent) => void
 *     hoverEnd: (event: MouseEvent) => void
 *     hover: (event: MouseEvent) => void
 *     resetImage: () => void
 *     zoomOnDemand: (event: MouseEvent | TouchEvent) => void
 * }}
 */
export const useImageZoomerGestures = (imageContainer, { isTopLevelZoomerClick }, stopAutoPlay, zoomStates) => {
  const position = ref(getDefaultPosition());
  const imageEl = ref(null);
  const ticking = ref(false);
  const isAnimating = ref(false);
  let initialScale = null;
  let momentumPanInterval = null;
  const isTopLevelZoomer = isTopLevelZoomerClick;

  const requestImageUpdate = () => {
    requestElementUpdate(ticking, () => updateImageTransform(imageEl.value, position.value, zoomStates.value));
  };

  const resetPosition = () => {
    position.value = getDefaultPosition();
  };

  /**
   *
   * @param {{posX: number, posY: number}} requestedPosition
   * @param {object} options
   * @param {boolean} [options.forceBoundary]
   * @param {boolean} [options.maintainBoundary]
   * @param {boolean} [options.atHorBoundaries]
   * @param {boolean} [options.atVertBoundaries]
   * @returns {ImagePosition & ZoomStates | undefined}
   */
  const updatePosition = (requestedPosition, options = {}) => {
    const { posX: requestedX, posY: requestedY } = requestedPosition;
    const { isZoomedIn, boundaries } = zoomStates.value;
    if (Number.isNaN(requestedX) || Number.isNaN(requestedY) || !isZoomedIn) {
      return;
    }
    const finalPosition = getFinalPosition(requestedPosition, position.value, boundaries, options);
    position.value = Object.assign(zoomStates.value, finalPosition, {
      atLeft: boundaries.xMax === finalPosition.posX,
      atRight: boundaries.xMin === finalPosition.posX,
      atTop: boundaries.yMax === finalPosition.posY,
      atBottom: boundaries.yMin === finalPosition.posY,
    });
  };
  /**
   *
   * @param {HTMLElement} targetEl
   */
  const updateCurrentImage = (targetEl) => {
    if (isTopLevelZoomer) {
      updatePictureWid(targetEl);
    }
    imageEl.value = targetEl;
  };

  const clearMomentumPanInterval = () => {
    clearInterval(momentumPanInterval);
  };

  /**
   *
   * @type {(velX: number, velY:number) => void}
   */
  const momentumPan = (velX, velY) => {
    const intervalTime = 1000 / 120;
    let speedX = velX * INITIAL_MAGNITUDE;
    let speedY = velY * INITIAL_MAGNITUDE;
    clearMomentumPanInterval();

    momentumPanInterval = setInterval(() => {
      if (speedX === 0 && speedY === 0) {
        clearMomentumPanInterval();
        return;
      }

      const currentPosition = position.value;

      updatePosition({
        posX: currentPosition.posX + speedX,
        posY: currentPosition.posY + speedY,
      });

      const velocityChange = VELOCITY_OFFSET * (intervalTime / 1000);

      speedX = closerToZero(speedX, velocityChange);
      speedY = closerToZero(speedY, velocityChange);

      requestImageUpdate();
    }, intervalTime);
  };

  /**
   *
   * @param {() => void} [callback]
   */
  const animateImageTransform = (instant, callback) => {
    if (isAnimating.value) {
      return;
    }
    isAnimating.value = true;
    if (imageEl.value) {
      imageEl.value.style.transition = `all ${isTopLevelZoomer && instant ? '0' : ANIMATION_TIME / 1000}s`;
    }
    requestImageUpdate();

    setTimeout(
      () => {
        isAnimating.value = false;
        if (imageEl?.value) {
          imageEl.value.style.transition = 'none';
        }
        if (typeof callback === 'function') {
          callback();
        }
      },
      isTopLevelZoomer ? 50 : ANIMATION_TIME,
    );
  };

  /**
   *
   * @type {(scale: number) => void}
   */
  const updateScale = (scale) => {
    const prevIsZoomedIn = zoomStates.value.isZoomedIn;
    const isZoomedIn = scale !== 1;

    zoomStates.value.isZoomedIn = isZoomedIn;
    zoomStates.value.scale = scale;
    if (prevIsZoomedIn !== isZoomedIn) {
      let zoomStartValues;

      if (!isZoomedIn) {
        zoomStartValues = {};
      } else {
        const viewportHeight = imageContainer?.value.getBoundingClientRect().height;
        const containerClientRect = imageEl.value.getBoundingClientRect();
        let vpLeftToImageCenterX = containerClientRect.left + containerClientRect.width / 2;
        let vpRightToImageCenterX = containerClientRect.right - containerClientRect.width / 2;
        let vpTopToImageCenterY = containerClientRect.height / 2 + containerClientRect.top;
        let vpBottomToImageCenterY = viewportHeight - vpTopToImageCenterY;

        if (isTopLevelZoomer) {
          vpLeftToImageCenterX = containerClientRect.width / 2;
          vpRightToImageCenterX = containerClientRect.width / 2;
          vpTopToImageCenterY = containerClientRect.height / 2;
          vpBottomToImageCenterY = containerClientRect.height / 2;
        }

        zoomStartValues = {
          imageWidth: imageEl.value.clientWidth,
          imageHeight: imageEl.value.clientHeight,
          vpRightToImageCenterX,
          vpLeftToImageCenterX,
          vpTopToImageCenterY,
          vpBottomToImageCenterY,
        };
      }

      zoomStates.value.zoomStartValues = zoomStartValues;
    }
    zoomStates.value.boundaries = getBoundaryPositions(scale, zoomStates.value.zoomStartValues, isTopLevelZoomer);
  };

  const resetImage = (withoutAnimation) => {
    if (!zoomStates.value.isZoomedIn) {
      return;
    }
    resetPosition();
    updateScale(DEFAULT_SCALE);
    animateImageTransform(withoutAnimation, () => {
      imageEl.value = null;
    });
  };
  /**
   *
   * @type {(event: Event | TouchEvent) => void}
   */
  const tapStart = (event) => {
    if (event.target === event.currentTarget) return;
    clearMomentumPanInterval();
  };

  /**
   *
   * @type {(event: TouchEvent) => void}
   */
  const pinchStart = (event) => {
    const targetEl = event.touches[0]?.target;
    updateCurrentImage(targetEl);
    clearMomentumPanInterval();
    initialScale = zoomStates.value.scale;
  };
  /**
   *
   * @type {(event: CustomZoomEvent) => void}
   */
  const pinch = (event) => {
    const prevScale = zoomStates.value.scale;
    initialScale = initialScale || prevScale;

    const scale = getBoundedValue(initialScale * event.deltaScale, 1, MAX_ZOOM_SCALE);

    if (scale === 1) {
      resetPosition();
      updateScale(DEFAULT_SCALE);
      requestImageUpdate();
    } else {
      updateScale(scale);

      if (event.deltaScale < 1) {
        updatePosition(position.value, {
          forceBoundary: !!position.value?.isMaintainingBoundary,
          maintainBoundary: true,
        });
      }

      requestImageUpdate();
    }
  };

  /**
   *
   * @type {(event: CustomZoomEvent) => void}
   */
  const pinchEnd = (event) => {
    const velocity = event.velFingers;

    if (Math.abs(velocity) < VELOCITY_MAX) {
      return;
    }

    const scale = velocity < 0 ? 1 : MAX_ZOOM_SCALE;

    if (scale === 1) {
      resetImage();
    } else {
      updateScale(scale);
      requestImageUpdate();
    }
  };

  /**
   *
   * @type {(event: MouseEvent) => void}
   */
  const mouseMove = (event) => {
    const { isZoomedIn, boundaries } = zoomStates.value;
    if (!isZoomedIn) {
      return;
    }
    if (isTopLevelZoomer) {
      const rect = imageContainer.value.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top + 2;

      updatePosition(
        {
          posX: mouseTargetPosition(x, rect.width, boundaries.xMin, boundaries.xMax),
          posY: mouseTargetPosition(y, rect.height, boundaries.yMin, boundaries.yMax, true),
        },
        { atHorBoundaries: boundaries.xMin > 0, atVertBoundaries: boundaries.yMin > 0 },
      );
    } else {
      updatePosition(
        {
          posX: mouseTargetPosition(event.clientX, window.innerWidth, boundaries.xMin, boundaries.xMax),
          posY: mouseTargetPosition(event.clientY, window.innerHeight, boundaries.yMin, boundaries.yMax),
        },
        { atHorBoundaries: boundaries.xMin > 0, atVertBoundaries: boundaries.yMin > 0 },
      );
    }

    requestImageUpdate();
  };
  /**
   *
   * @type {(event: CustomZoomEvent) => void}
   */
  const pan = (event) => {
    if (event.type === 'mousemove' || !zoomStates.value.isZoomedIn) {
      return;
    }

    const currentPosition = position.value;

    updatePosition({
      posX: currentPosition.posX + event.deltaX,
      posY: currentPosition.posY + event.deltaY,
    });

    requestImageUpdate();
  };

  /**
   *
   * @type {(event: CustomZoomEvent) => void}
   */
  const panEnd = (event) => {
    if (event.type === 'mouseup' || !zoomStates.value.isZoomedIn) {
      return;
    }
    momentumPan(event.velX, event.velY);
  };

  /**
   *
   * @param {MouseEvent | TouchEvent} event
   */
  const zoomOnDemand = (event) => {
    updateScale(MAX_ZOOM_SCALE);
    const { boundaries } = zoomStates.value;
    if (isTopLevelZoomer) {
      const rect = imageContainer.value.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top + 2;
      updatePosition(
        {
          posX: mouseTargetPosition(x, rect.width, boundaries.xMin, boundaries.xMax),
          posY: mouseTargetPosition(y, rect.height, boundaries.yMin, boundaries.yMax, true),
        },
        { atHorBoundaries: boundaries.xMin > 0, atVertBoundaries: boundaries.yMin > 0 },
      );
    } else {
      updatePosition(
        {
          posX: mouseTargetPosition(event.clientX, window.innerWidth, boundaries.xMin, boundaries.xMax),
          posY: mouseTargetPosition(event.clientY, window.innerHeight, boundaries.yMin, boundaries.yMax),
        },
        { atHorBoundaries: boundaries.xMin > 0, atVertBoundaries: boundaries.yMin > 0 },
      );
    }
    animateImageTransform();
  };

  /**
   *
   * @type {(event: MouseEvent | TouchEvent) => void}
   */
  const tap = async (event) => {
    if (event.target.tagName !== 'IMG') {
      return;
    }
    updateCurrentImage(event.target);
    if (isAnimating.value) {
      return;
    }
    if (zoomStates.value.scale >= MAX_ZOOM_SCALE) {
      resetImage();
      return;
    }

    zoomOnDemand(event);
  };

  /**
   *
   * @type {(event: KeyboardEvent) => void}
   */
  const onEnter = (event) => {
    updateCurrentImage(event.target.querySelector('img'));
    if (zoomStates.value.scale >= MAX_ZOOM_SCALE) {
      resetImage();
      return;
    }
    updateScale(MAX_ZOOM_SCALE);
    const { boundaries } = zoomStates.value;
    updatePosition(
      {
        posX: getCenterPosition(boundaries.xMin, boundaries.xMax),
        posY: getCenterPosition(boundaries.yMin, boundaries.yMax),
      },
      { forceBoundary: true },
    );
    animateImageTransform();
  };

  /**
   *
   * @type {(event: KeyboardEvent) => void}
   */
  const onArrowPressed = (event) => {
    const { isZoomedIn } = zoomStates.value;

    if (!isZoomedIn || !MOVEMENT_MAP[event.key]) {
      return;
    }
    const { vert, hor } = MOVEMENT_MAP[event.key];

    updatePosition({
      posX: position.value.posX + hor,
      posY: position.value.posY + vert,
    });

    requestImageUpdate();
  };

  /**
   *
   * @type {(event: MouseEvent) => void}
   */
  const hover = useDebounceFn((event) => {
    const hoveredElement = event.target;
    if (hoveredElement.classList.contains('picture-image')) {
      updateCurrentImage(hoveredElement);
      updateScale(MAX_ZOOM_SCALE);
    }
  }, 20); // On hover of alt image it's shown in main area
  // Added a delay before zoom behaviour kicks in so when you are in main image area
  // image has time to switch back to selected one, otherwise breaks

  /**
   *
   * @type {(event: MouseEvent) => void}
   */
  const hoverEnd = () => {
    resetImage(isTopLevelZoomer);
  };

  return {
    zoomStates,
    isAnimating,
    tapStart,
    pinchStart,
    pinch,
    pinchEnd,
    mouseMove,
    pan,
    panEnd,
    tap,
    onEnter,
    onArrowPressed,
    hover,
    hoverEnd,
    resetImage,
    zoomOnDemand,
  };
};

export default useImageZoomerGestures;
